import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Mail, Users, MessageSquare, Star, Download, LogOut, Plus, Trash2, Eye, EyeOff, Settings as SettingsIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const StatTile = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase()}`}>
    <span className={`h-12 w-12 rounded-xl ${color} text-white inline-flex items-center justify-center`}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  </div>
);

function JobForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    try {
      await api.post("/admin/jobs", {
        title: fd.get("title"),
        job_type: fd.get("job_type"),
        experience: fd.get("experience"),
        locations: (fd.get("locations") || "").split(",").map((s) => s.trim()).filter(Boolean),
        ctc: fd.get("ctc") || null,
        domain: fd.get("domain") || null,
        description: fd.get("description") || "",
        skills: (fd.get("skills") || "").split(",").map((s) => s.trim()).filter(Boolean),
        is_active: true,
      });
      toast.success("Job created");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full" data-testid="admin-add-job-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Job
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="admin-job-form">
          <div><Label>Title</Label><Input name="title" required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Job Type</Label>
              <select name="job_type" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" required>
                <option>Full Time</option><option>Urgent</option><option>Active Hiring</option><option>Contract</option>
              </select>
            </div>
            <div><Label>Experience</Label><Input name="experience" placeholder="3-5 years" required /></div>
          </div>
          <div><Label>Locations (comma-separated)</Label><Input name="locations" placeholder="Bengaluru, Pune" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CTC</Label><Input name="ctc" placeholder="10-15 LPA" /></div>
            <div><Label>Domain</Label><Input name="domain" placeholder="IT & Technology" /></div>
          </div>
          <div><Label>Skills (comma-separated)</Label><Input name="skills" placeholder="Java, Spring, AWS" /></div>
          <div><Label>Description</Label><Textarea name="description" rows={4} /></div>
          <Button type="submit" disabled={submitting} className="w-full bg-bee-teal text-white rounded-full">{submitting ? "Saving..." : "Create"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TestimonialForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.post("/admin/testimonials", {
        quote: fd.get("quote"),
        author_name: fd.get("author_name"),
        author_title: fd.get("author_title"),
        rating: parseInt(fd.get("rating") || "5", 10),
        is_published: true,
      });
      toast.success("Testimonial added");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-bee-orange text-white hover:bg-bee-orange/90 rounded-full" data-testid="admin-add-testimonial-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Testimonial
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Testimonial</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div><Label>Quote</Label><Textarea name="quote" rows={4} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Author</Label><Input name="author_name" required /></div>
            <div><Label>Title</Label><Input name="author_title" required /></div>
          </div>
          <div><Label>Rating (1-5)</Label><Input name="rating" type="number" min="1" max="5" defaultValue="5" /></div>
          <Button type="submit" className="w-full bg-bee-teal text-white rounded-full">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const downloadResume = async (kind, id, filename) => {
  try {
    const token = localStorage.getItem("wb_token");
    const res = await fetch(`${API}/admin/resume/${kind}/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "resume";
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not download resume");
  }
};

export default function AdminDashboardPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [navMenu, setNavMenu] = useState([]);
  const [savingMenu, setSavingMenu] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const refresh = async () => {
    try {
      const [j, i, c, a, t, s] = await Promise.all([
        api.get("/admin/jobs"),
        api.get("/admin/inquiries"),
        api.get("/admin/candidates"),
        api.get("/admin/applications"),
        api.get("/admin/testimonials"),
        api.get("/settings"),
      ]);
      setJobs(j.data);
      setInquiries(i.data);
      setCandidates(c.data);
      setApplications(a.data);
      setTestimonials(t.data);
      setNavMenu(s.data?.nav_menu || []);
    } catch (err) {
      // 401 handled by redirect
    }
  };

  const toggleNavItem = (id) => {
    setNavMenu((prev) => prev.map((m) => m.id === id ? { ...m, visible: !m.visible } : m));
  };

  const updateNavItem = (id, changes) => {
    setNavMenu((prev) => prev.map((m) => m.id === id ? { ...m, ...changes } : m));
  };

  const saveNavMenu = async () => {
    const invalid = navMenu.find((item) => {
      const type = item.link_type || (item.kind === "link" ? "page" : "anchor");
      const path = item.path || (type === "page" ? "/jobs" : `#${item.id}`);
      return type === "page" ? !/^\/(?!\/)[a-zA-Z0-9/_-]*$/.test(path) : !/^#[-a-zA-Z0-9]+$/.test(path);
    });
    if (invalid) {
      toast.error(`Enter a valid ${invalid.label} path before saving.`);
      return;
    }
    setSavingMenu(true);
    try {
      await api.put("/admin/settings", { nav_menu: navMenu });
      toast.success("Header menu updated. Public site will reflect changes immediately.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingMenu(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/admin/jobs/${id}`);
    refresh();
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    refresh();
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
      <header className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="WorkingBees" className="h-10 w-10 object-contain rounded-md bg-white p-0.5" />
            <span className="font-display font-bold text-lg text-bee-orange">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user.email}</span>
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn" className="rounded-full">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dashboard</h1>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatTile icon={Briefcase} label="Jobs" value={jobs.length} color="bg-bee-teal" />
          <StatTile icon={Mail} label="Inquiries" value={inquiries.length} color="bg-bee-orange" />
          <StatTile icon={Users} label="Candidates" value={candidates.length} color="bg-bee-teal" />
          <StatTile icon={MessageSquare} label="Applications" value={applications.length} color="bg-bee-orange" />
          <StatTile icon={Star} label="Testimonials" value={testimonials.length} color="bg-bee-honey" />
        </div>

        <Tabs defaultValue="jobs" className="mt-8" data-testid="admin-tabs">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="jobs" data-testid="admin-tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="candidates" data-testid="admin-tab-candidates">Candidates</TabsTrigger>
            <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-4">
            <div className="flex justify-end mb-3"><JobForm onCreated={refresh} /></div>
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Type</th><th className="text-left p-3">Locations</th><th className="text-left p-3">CTC</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="border-t border-border" data-testid={`admin-job-row-${j.id}`}>
                      <td className="p-3 font-semibold">{j.title}</td>
                      <td className="p-3">{j.job_type}</td>
                      <td className="p-3">{(j.locations || []).join(", ")}</td>
                      <td className="p-3">{j.ctc || "-"}</td>
                      <td className="p-3 text-right"><Button variant="ghost" size="sm" onClick={() => deleteJob(j.id)} data-testid={`admin-delete-job-${j.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                    </tr>
                  ))}
                  {jobs.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-muted-foreground">No jobs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-4">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Type</th><th className="text-left p-3">Message</th><th className="text-left p-3">When</th></tr>
                </thead>
                <tbody>
                  {inquiries.map((i) => (
                    <tr key={i.id} className="border-t border-border" data-testid={`admin-inquiry-${i.id}`}>
                      <td className="p-3 font-semibold">{i.name}</td>
                      <td className="p-3">{i.email}</td>
                      <td className="p-3">{i.phone || "-"}</td>
                      <td className="p-3">{i.inquirer_type || "-"}</td>
                      <td className="p-3 max-w-xs truncate" title={i.message}>{i.message}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-muted-foreground">No inquiries yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="candidates" className="mt-4">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email / Phone</th><th className="text-left p-3">Title</th><th className="text-left p-3">Skills</th><th className="text-left p-3">Resume</th></tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.id} className="border-t border-border" data-testid={`admin-candidate-${c.id}`}>
                      <td className="p-3 font-semibold">{c.full_name}</td>
                      <td className="p-3"><div>{c.email}</div><div className="text-xs text-muted-foreground">{c.phone}</div></td>
                      <td className="p-3">{c.current_title || "-"}</td>
                      <td className="p-3 max-w-xs truncate">{c.skills || "-"}</td>
                      <td className="p-3">{c.resume_path ? <Button variant="outline" size="sm" onClick={() => downloadResume("candidate", c.id, c.resume_filename)} data-testid={`admin-cand-resume-${c.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Resume</Button> : "-"}</td>
                    </tr>
                  ))}
                  {candidates.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-muted-foreground">No candidates yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="applications" className="mt-4">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Job</th><th className="text-left p-3">Candidate</th><th className="text-left p-3">Email / Phone</th><th className="text-left p-3">When</th><th className="text-left p-3">Resume</th></tr>
                </thead>
                <tbody>
                  {applications.map((a) => (
                    <tr key={a.id} className="border-t border-border" data-testid={`admin-app-${a.id}`}>
                      <td className="p-3 font-semibold">{a.job_title}</td>
                      <td className="p-3">{a.full_name}</td>
                      <td className="p-3"><div>{a.email}</div><div className="text-xs text-muted-foreground">{a.phone}</div></td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-3">{a.resume_path ? <Button variant="outline" size="sm" onClick={() => downloadResume("application", a.id, a.resume_filename)} data-testid={`admin-app-resume-${a.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Resume</Button> : "-"}</td>
                    </tr>
                  ))}
                  {applications.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-muted-foreground">No applications yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-4">
            <div className="flex justify-end mb-3"><TestimonialForm onCreated={refresh} /></div>
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-testimonial-${t.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-bee-orange text-bee-orange" />)}</div>
                    <Button variant="ghost" size="sm" onClick={() => deleteTestimonial(t.id)} data-testid={`admin-delete-testimonial-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                  <p className="mt-3 text-sm">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-3 pt-3 border-t border-border text-xs">
                    <div className="font-semibold">{t.author_name}</div>
                    <div className="text-muted-foreground">{t.author_title}</div>
                  </div>
                </div>
              ))}
              {testimonials.length === 0 && <div className="p-6 text-center text-muted-foreground col-span-2">No testimonials</div>}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <div className="rounded-2xl border border-border bg-card p-6 max-w-5xl" data-testid="admin-settings-panel">
              <div className="flex items-center gap-3 mb-1">
                <span className="h-10 w-10 rounded-lg bg-bee-teal text-white inline-flex items-center justify-center"><SettingsIcon className="h-5 w-5" /></span>
                <h2 className="font-display text-xl font-bold">Header Menu Visibility</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Toggle which items appear in the public website&apos;s header navigation. Changes apply instantly on save.</p>

              <div className="divide-y divide-border">
                {navMenu.map((item) => (
                  <div key={item.id} className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_150px_minmax(130px,1fr)_auto] sm:items-end" data-testid={`admin-nav-item-${item.id}`}>
                    <div className="flex items-center gap-3 sm:pb-2">
                      {item.visible ? <Eye className="h-4 w-4 text-bee-teal" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                      <div>
                        <div className="font-semibold">{item.label}</div>
                      </div>
                    </div>
                    <label className="text-xs font-medium text-muted-foreground">Link type
                      <select
                        value={item.link_type || (item.kind === "link" ? "page" : "anchor")}
                        onChange={(event) => updateNavItem(item.id, {
                          link_type: event.target.value,
                          kind: event.target.value === "page" ? "link" : "section",
                          path: event.target.value === "page" ? (item.id === "about" ? "/about-us" : item.id === "jobs" ? "/jobs" : "/") : `#${item.id}`,
                        })}
                        className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                        data-testid={`admin-nav-type-${item.id}`}
                      >
                        <option value="anchor">Anchor section</option>
                        <option value="page">Page</option>
                      </select>
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">Path
                      <Input
                        value={item.path || ((item.link_type || (item.kind === "link" ? "page" : "anchor")) === "page" ? "/jobs" : `#${item.id}`)}
                        onChange={(event) => updateNavItem(item.id, { path: event.target.value })}
                        className="mt-1"
                        placeholder={item.id === "about" ? "/about-us" : `#${item.id}`}
                        data-testid={`admin-nav-path-${item.id}`}
                      />
                    </label>
                    <Switch
                      checked={item.visible}
                      onCheckedChange={() => toggleNavItem(item.id)}
                      aria-label={`Show ${item.label} in header`}
                      data-testid={`admin-nav-toggle-${item.id}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={saveNavMenu}
                  disabled={savingMenu}
                  data-testid="admin-save-menu-btn"
                  className="bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full px-6"
                >
                  {savingMenu ? "Saving..." : "Save Menu"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
